/**
 * Token badges on UT player cards (design: WNF UT v2 token badges, Oct 2026):
 * a priority token in use for the next game, a shield protecting a streak,
 * an injury token, and unpaid games (design: XP Breakdown, Oct 2026) so it's
 * clear why someone's XP is lower. Pure: the data comes from useTokenBadges.
 */

export type TokenKind = 'priority' | 'shield' | 'injury' | 'unpaid';

export interface TokenBadge {
  k: TokenKind;
  /** Icon colour. */
  colour: string;
  /** Ring behind the icon: a solid colour, or a conic gradient showing shield recovery. */
  ring: string;
  /** Icon shape (CSS clip-path). */
  clip: string;
  /** A character drawn instead of the clipped shape (unpaid: "£"). */
  glyph?: string;
  /** Label on large cards: "PRIORITY", "SHIELD ON", "SHIELD 3/5", "INJURED", "RETURNING". */
  short: string;
  /** The detail shown on hover / tap. */
  tip: string;
}

/** Per-player inputs, all from current data (players row + the next game). */
export interface TokenInputs {
  /** Registered for the next game with a priority token. */
  priority?: boolean;
  /** players.shield_active, with protected_streak_value and current_streak. */
  shield?: { protectedStreak: number; currentStreak: number; usedForNextGame: boolean } | null;
  /** players.injury_token_active, with the streaks; returning = registered for the next game. */
  injury?: { originalStreak: number; returnStreak: number; returning: boolean } | null;
  /** Games still unpaid a day after kick-off (player_unpaid_games_view). */
  unpaid?: number;
}

const GOLD = '#e8c15a';
const SKY = '#9cc8ff';
const RED = '#ff6b5e';
const AMBER = '#f5a524';

export const TOKEN_GLYPHS: Record<TokenKind, string> = {
  priority: 'polygon(50% 0,100% 52%,68% 52%,68% 100%,32% 100%,32% 52%,0 52%)',
  shield: 'polygon(50% 0,100% 17%,100% 52%,50% 100%,0 52%,0 17%)',
  unpaid: 'none',
  injury: 'polygon(36% 0,64% 0,64% 36%,100% 36%,100% 64%,64% 64%,64% 100%,36% 100%,36% 64%,0 64%,0 36%,36% 36%)',
};

const games = (n: number) => `${n} more game${n === 1 ? '' : 's'}`;

export function tokenBadges(t: TokenInputs): TokenBadge[] {
  const out: TokenBadge[] = [];
  if (t.priority) {
    out.push({ k: 'priority', colour: GOLD, ring: GOLD, clip: TOKEN_GLYPHS.priority, short: 'PRIORITY', tip: 'Priority token: guaranteed a place in the next game' });
  }
  if (t.shield) {
    const { protectedStreak: prot, currentStreak: nat } = t.shield;
    // Same maths as useShieldStatus: the protected bonus decays as the natural streak
    // rebuilds, and the shield is done once natural >= ceil(protected / 2).
    const conv = Math.max(1, Math.ceil(prot / 2));
    if (t.shield.usedForNextGame || nat >= prot) {
      out.push({
        k: 'shield',
        colour: SKY,
        ring: SKY,
        clip: TOKEN_GLYPHS.shield,
        short: 'SHIELD ON',
        tip: `Shield token on: ${prot}-game streak protected while out this week`,
      });
    } else {
      const pct = Math.min(100, Math.round((nat / conv) * 100));
      out.push({
        k: 'shield',
        colour: SKY,
        ring: `conic-gradient(${SKY} 0 ${pct}%, rgba(156,200,255,.28) 0)`,
        clip: TOKEN_GLYPHS.shield,
        short: `SHIELD ${nat}/${conv}`,
        tip: `Shield recovering: ${prot}-game streak protected. ${games(conv - nat)} to fully recover`,
      });
    }
  }
  if (t.injury) {
    const back = t.injury.returning;
    out.push({
      k: 'injury',
      colour: back ? AMBER : RED,
      ring: back ? AMBER : RED,
      clip: TOKEN_GLYPHS.injury,
      short: back ? 'RETURNING' : 'INJURED',
      tip: `${back ? 'Back from injury this week' : 'Injured during WNF'}: returns at a ${t.injury.returnStreak}-game streak (was ${t.injury.originalStreak})`,
    });
  }
  if (t.unpaid && t.unpaid > 0) {
    const n = t.unpaid;
    out.push({
      k: 'unpaid',
      colour: RED,
      ring: RED,
      clip: TOKEN_GLYPHS.unpaid,
      glyph: '£',
      short: n > 1 ? `${n} UNPAID` : 'UNPAID',
      // Same −50% per game as the XP modifier (utils/xpBreakdown.ts).
      tip: `${n} unpaid game${n === 1 ? '' : 's'}: XP bonuses down ${n * 50}% until ${n === 1 ? "it's" : "they're"} paid`,
    });
  }
  return out;
}
